import type { ITreeMenu } from '@refinedev/core';
import type { Permission } from 'interfaces';
import { useMemo } from 'react';
import { useIsAdmin } from './useIsAdmin';

interface useFilteredMenuItemsProps {
  permissions: Permission[] | undefined;
  menuItems: ITreeMenu[] | undefined;
}

export const ADMIN_HIDDEN_MENU_RESOURCES = new Set([
  'uploads',
  'claims',
  'remittances',
  'submissions',
  'invoices',
]);
export const ADMIN_ONLY_MENU_RESOURCES = new Set(['provider-dashboard']);

export const useFilteredMenuItems = ({
  permissions = [],
  menuItems = [],
}: useFilteredMenuItemsProps) => {
  const isAdmin = useIsAdmin();

  // Memoize the permissions set to prevent unnecessary recalculations
  const permissionSet = useMemo(() => {
    if (permissions.length > 0) {
      return new Set(permissions.map((permission) => permission.resource));
    }
    return new Set<string>();
  }, [permissions]);

  // Memoize the filtered menu items based on permissions and isAdmin status
  const filteredMenuItems = useMemo(() => {
    if (!menuItems || menuItems.length === 0) return [];

    if (isAdmin) {
      return menuItems.filter(
        (menuItem) =>
          !ADMIN_HIDDEN_MENU_RESOURCES.has(menuItem.name.toLowerCase())
      );
    }

    if (!isAdmin && permissions.length > 0) {
      return menuItems.filter((menuItem) => {
        const resource = menuItem.name.toLowerCase();

        return (
          !ADMIN_ONLY_MENU_RESOURCES.has(resource) &&
          permissionSet.has(resource)
        );
      });
    }

    // If no permissions are specified, return all menu items
    return menuItems.filter(
      (menuItem) => !ADMIN_ONLY_MENU_RESOURCES.has(menuItem.name.toLowerCase())
    );
  }, [menuItems, isAdmin, permissionSet, permissions]);

  return filteredMenuItems;
};
